import { Injectable } from '@angular/core';

import { SoftwareSubscription, SoftwareSubscriptionStatus } from '@hdms/shared/common-types';
import { EnvironmentService, NumericDateFormatPipe } from '@hdms/shared/util-core';

import { StatusNotification, StatusNotificationMessage } from '../helper';
import { IsCanceledSubscriptionStatusPipe, IsModifiedSubscriptionPipe } from '../pipes';

const { CANCELED, NOT_STARTED, PENDING } = SoftwareSubscriptionStatus;

@Injectable({
  providedIn: 'root'
})
export class SubscriptionStatusNotifcationService {
  private readonly hqTimeZone = this.environmentService.hqTimeZone;

  constructor(
    private readonly environmentService: EnvironmentService,
    private readonly isCanceledSubscriptionStatusPipe: IsCanceledSubscriptionStatusPipe,
    private readonly isModifiedSubscriptionPipe: IsModifiedSubscriptionPipe,
    private readonly numericDateFormatPipe: NumericDateFormatPipe
  ) {}

  getStatusNotificationData(
    switchCondition: boolean,
    subscription: SoftwareSubscription,
    statusNotificationData: StatusNotification[]
  ): StatusNotification {
    switch (switchCondition) {
      case this.isModifiedSubscriptionPipe.transform(subscription):
      case this.isCanceledSubscriptionStatusPipe.transform(subscription.status):
      case subscription.status === NOT_STARTED:
      case subscription.status === PENDING: {
        return this.findStatusNotificationData(statusNotificationData, subscription.status);
      }
      default: {
        return null;
      }
    }
  }

  getProcessedTranslationStatusNotificationData(
    switchCondition: boolean,
    subscription: SoftwareSubscription,
    statusNotificationData: StatusNotification[]
  ): StatusNotification {
    switch (switchCondition) {
      case this.isCanceledSubscriptionStatusPipe.transform(subscription.status): {
        const statusNotificationItemData = this.findStatusNotificationData(statusNotificationData, CANCELED);

        return {
          ...statusNotificationItemData,
          translation: {
            ...(statusNotificationItemData.translation as StatusNotificationMessage),
            formattedDate: {
              validUntilDate: this.numericDateFormatPipe.transform(subscription.validUntil, {
                timezone: this.hqTimeZone
              }),
              interpolation: { escapeValue: false }
            }
          }
        };
      }
      case subscription.status === NOT_STARTED: {
        const statusNotificationItemData = this.findStatusNotificationData(statusNotificationData, NOT_STARTED);

        return {
          ...statusNotificationItemData,
          translation: {
            ...(statusNotificationItemData.translation as StatusNotificationMessage),
            formattedDate: {
              startDate: this.numericDateFormatPipe.transform(subscription.startDate, {
                timezone: this.hqTimeZone
              }),
              interpolation: { escapeValue: false }
            }
          }
        };
      }
      default: {
        return null;
      }
    }
  }

  findStatusNotificationData(
    statusNotificationData: StatusNotification[],
    status: SoftwareSubscriptionStatus
  ): StatusNotification {
    return statusNotificationData.find(data => data.status === status);
  }
}
